import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ApplicationResponse } from "@src/common/domain/common.model";
import api from "@src/common/services/api.service";
import AppConstants from "@src/utils/app.constants";
import {
  ProjectListModelRef,
  ProjectListStore,
} from "../domain/project-list.model";

export const fetchProjectList = createAsyncThunk<
  ProjectListModelRef[],
  { type: "npms" | "eci" | "custom" }
>("projectList/fetchAllProjects", async ({ type }) => {
  const url = `${AppConstants.api}/${type}/list`;
  const response = await api.get<ApplicationResponse<ProjectListModelRef[]>>(
    url
  );
  return response.data.data || [];
});


export const fetchDivisionProjectList = createAsyncThunk<
  ProjectListModelRef[],
  { projectType: "npms" | "eci" | "custom" , division: string; plantIds: string[] }
>("projectList/fetchProjectsByDivision", async ({ projectType,division,plantIds }) => {
  const url = `${AppConstants.api}/dashboard/get-projects-by-division?projectType=${projectType}&division=${division}`;
  const response = await api.post<ApplicationResponse<ProjectListModelRef[]>>(
    url, plantIds
  );
  return response.data.data || [];
});

export const fetchCustomerProjectList = createAsyncThunk<
  ProjectListModelRef[],
  { projectType: "npms" | "eci" | "custom" , status: string , customerId:string; plantIds: string[] }
>("projectList/fetchProjectsByCustomer", async ({ projectType,status,customerId,plantIds }) => {
  const url = `${AppConstants.api}/dashboard/get-projects-by-customer?projectType=${projectType}&projectStatus=${status}&customerId=${customerId}`;
  const response = await api.post<ApplicationResponse<ProjectListModelRef[]>>(
    url,plantIds
  );
  return response.data.data || [];
});
export const fetchProductProjectList = createAsyncThunk<
  ProjectListModelRef[],
  { projectType: "npms" | "eci" | "custom" , status: string , productId:string; plantIds: string[] }
>("projectList/fetchProjectsByProduct", async ({ projectType,status,productId,plantIds = [] }) => {
  const url = `${AppConstants.api}/dashboard/get-projects-by-product?projectType=${projectType}&projectStatus=${status}&productId=${productId}`;
  const response = await api.post<ApplicationResponse<ProjectListModelRef[]>>(
    url,plantIds
  );
  return response.data.data || [];
});

export const fetchRankingProjectList = createAsyncThunk<
  ProjectListModelRef[],
  { projectType: "npms" | "eci" | "custom" , status: string , escRanking:string; plantIds: string[] }
>("projectList/fetchProjectsByRanking", async ({ projectType,status,escRanking,plantIds = [] }) => {
  const url = `${AppConstants.api}/dashboard/get-projects-by-esc?projectType=${projectType}&projectStatus=${status}&escRanking=${escRanking}&plantIds=$`;
  const response = await api.post<ApplicationResponse<ProjectListModelRef[]>>(
    url,plantIds
  );
  return response.data.data || [];
});

export const fetchUsersProjectsList = createAsyncThunk<
  ProjectListModelRef[],
  { projectType: "npms" | "eci" | "custom" , id: string , status?: string | null; plantIds: string[]  }
>("projectList/fetchUsersProjectsList", async ({ projectType,id,status="",plantIds }) => {
  let url = `${AppConstants.api}/dashboard/user-projects?projectType=${projectType}&id=${id}`;
  if(status && status!=="undefined") url = url+ `&status=${status}`
  const response = await api.post<ApplicationResponse<ProjectListModelRef[]>>(
    url,plantIds
  );
  return response.data.data || [];
});
export const fetchTotalProjectsList = createAsyncThunk<
  ProjectListModelRef[],
  { projectType: "npms" | "eci" | "custom" , status?: string | null,plantIds: string[] }
>("projectList/fetchTotalProjectsList", async ({ projectType,status="",plantIds }) => {
  let url = `${AppConstants.api}/dashboard/project?projectType=${projectType}`;
  if( status && status!=="undefined"){
   url = url+ `&status=${status}`;
  }
  const response = await api.post<ApplicationResponse<ProjectListModelRef[]>>(
    url,plantIds
  );
  return response.data.data || [];
});


const ProjectListSlice = createSlice({
  name: "projectList",
  initialState: {
    minimal: { status: "loading", error: null, data: [] },
  } as ProjectListStore,
  reducers: {
    resetState: (state) => {
      return {
        minimal: { status: "loading", error: null, data: [] },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTotalProjectsList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchTotalProjectsList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchTotalProjectsList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error.message,
        };
      })
      .addCase(fetchDivisionProjectList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchDivisionProjectList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchDivisionProjectList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error.message,
        };
      })
      .addCase(fetchCustomerProjectList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchCustomerProjectList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchCustomerProjectList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error.message,
        };
      })
      .addCase(fetchProductProjectList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchProductProjectList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchProductProjectList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error.message,
        };
      })
      .addCase(fetchRankingProjectList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchRankingProjectList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchRankingProjectList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error.message,
        };
      })
      .addCase(fetchUsersProjectsList.pending, (state, action) => {
        state.minimal = {
          status: "loading",
          data: [],
          error: null,
        };
      })
      .addCase(fetchUsersProjectsList.fulfilled, (state, action) => {
        state.minimal = {
          status: "idle",
          data: action.payload,
          error: null,
        };
      })
      .addCase(fetchUsersProjectsList.rejected, (state, action) => {
        state.minimal = {
          status: "failed",
          data: [],
          error: action.error.message,
        };
      })
  },
});

export const { resetState } = ProjectListSlice.actions;
export default ProjectListSlice.reducer;
